import { Outlet, useParams, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import { clearSession } from '../../lib/session';
import LoadingOverlay from '../base/LoadingOverlay';
import { useDeviceMode } from '../../hooks/useDeviceMode';
import ClientPortalDesktopLayout from '../client/ClientPortalDesktopLayout';
import ClientPortalMobileLayout from '../client/ClientPortalMobileLayout';
import { PortalCustomizationContext, type PortalCustomization } from '../../hooks/usePortalCustomization';

export default function ClientLayout() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useDeviceMode();
  const [isValidating, setIsValidating] = useState(true);
  const [isValidToken, setIsValidToken] = useState(false);
  const [clientName, setClientName] = useState('');
  const [customization, setCustomization] = useState<PortalCustomization>({
    backgroundUrl: null,
    welcomeMessage: null,
    accentColor: null,
  });

  // ✅ Bloquear cambio de layout en dispositivos táctiles reales (teléfonos/tablets).
  //    En estos dispositivos, rotar el celular NO debe desmontar el Outlet ni el reproductor.
  //    YouTube/Netflix/etc no cambian de layout al rotar — mismo principio.
  const isTouchDevice = useMemo(() => {
    return typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0);
  }, []);

  // ✅ Debug preview mode: cuando el DVD se carga con debugMobilePreview=1 dentro de un iframe,
  //    forzamos layout mobile siempre. La rotación solo cambia CSS, no el árbol React.
  const isDebugMobilePreview = location.search.includes('debugMobilePreview=1');

  useEffect(() => {
    validateAccess();
  }, [token]);

  const validateAccess = async () => {
    try {
      setIsValidating(true);

      if (!token) {
        setIsValidToken(false);
        setIsValidating(false);
        return;
      }

      // Step 1: validate token and get client_id
      const { data: tokenData, error: tokenError } = await supabase
        .from('client_access_tokens')
        .select('id, is_active, client_id')
        .eq('token', token)
        .maybeSingle();

      if (tokenError) {
        console.error('Error validando token:', tokenError);
        setIsValidToken(false);
        return;
      }

      if (!tokenData || !tokenData.is_active) {
        setIsValidToken(false);
        return;
      }

      // Step 2: fetch client data directly (avoids PostgREST join schema cache issues)
      const { data: clientData, error: clientError } = await supabase
        .from('clients')
        .select('full_name, portal_background_url, portal_welcome_message, portal_accent_color')
        .eq('id', tokenData.client_id)
        .maybeSingle();

      if (clientError) {
        console.error('Error cargando datos del cliente:', clientError);
      }

      setIsValidToken(true);
      setClientName(clientData?.full_name || 'Cliente');
      setCustomization({
        backgroundUrl: clientData?.portal_background_url || null,
        welcomeMessage: clientData?.portal_welcome_message || null,
        accentColor: clientData?.portal_accent_color || null,
      });
    } catch (err: any) {
      console.error('Error validando acceso:', err);
      setIsValidToken(false);
    } finally {
      setIsValidating(false);
    }
  };

  const handleExit = () => {
    clearSession();
    navigate('/');
  };

  // Estado de carga
  if (isValidating) {
    return <LoadingOverlay message="Validando acceso..." />;
  }

  // Token inválido
  if (!isValidToken) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-white flex items-center justify-center p-6">
        <div className="max-w-lg w-full bg-white rounded-2xl shadow-2xl p-10">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-lock-line text-4xl text-red-600"></i>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-3">Acceso No Válido</h1>
            <p className="text-gray-600 mb-4">
              El enlace de acceso es inválido o ha sido desactivado por seguridad.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
              <p className="text-sm text-blue-800 flex items-start gap-2">
                <i className="ri-information-line text-lg flex-shrink-0 mt-0.5"></i>
                <span>
                  <strong>Tu token no está activo.</strong> Por favor, solicita acceso a tus fotos y videos contactando a Fotos y Video Santa Rosa.
                </span>
              </p>
            </div>
          </div>

          <div className="space-y-3 mb-6">
            <a
              href="https://wa.me/17078066168"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-2xl"></i>
              <span>Contactar por WhatsApp</span>
            </a>

            <a
              href="tel:+17078066168"
              className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-lg transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line text-xl"></i>
              <span>Llamar: (707) 806-6168</span>
            </a>
          </div>

          <button
            onClick={handleExit}
            className="w-full px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-home-line"></i>
            <span>Ir al inicio</span>
          </button>
        </div>
      </div>
    );
  }

  // ✅ Si es dispositivo táctil real o estamos en debug preview, SIEMPRE usar layout mobile
  if (isTouchDevice || isDebugMobilePreview) {
    return (
      <PortalCustomizationContext.Provider value={customization}>
        <ClientPortalMobileLayout
          clientName={clientName}
          handleExit={handleExit}
          customization={customization}
        />
      </PortalCustomizationContext.Provider>
    );
  }

  // Layout adaptativo: solo para dispositivos NO táctiles y NO debug (desktop/laptop con resize)
  if (isMobile && !isDebugMobilePreview) {
    return (
      <PortalCustomizationContext.Provider value={customization}>
        <ClientPortalMobileLayout
          clientName={clientName}
          handleExit={handleExit}
          customization={customization}
        />
      </PortalCustomizationContext.Provider>
    );
  }

  return (
    <PortalCustomizationContext.Provider value={customization}>
      <ClientPortalDesktopLayout
        token={token || ''}
        clientName={clientName}
        handleExit={handleExit}
        customization={customization}
      />
    </PortalCustomizationContext.Provider>
  );
}